import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { publicApi, clientApi } from "@/lib/http";
import { ClientAuthDialog, ClientPortal } from "@/components/public/ClientPortal";
import { UpcomingPanel } from "@/components/public/UpcomingPanel";
import { RefusedDialog, refusedDetail } from "@/components/public/RefusedDialog";
import { RewardsPanel } from "@/components/public/RewardsPanel";
import { PackageCard, PackageDetails } from "@/components/public/PackageCard";
import { fmtCurrency } from "@/lib/calc";
import { fmtDate } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Clock, Check, ChevronLeft, ChevronDown, CalendarDays, UserCircle, X, Plus } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { initTracking, trackBooking, trackDepositPaid, detectTrafficSource, captureAttribution, metaBrowserIds } from "@/lib/tracking";
import { AddToCalendar } from "@/components/public/AddToCalendar";

const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const addDaysStr = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export default function PublicBooking() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [paidDeposit, setPaidDeposit] = useState(null);
  useEffect(() => { if (data?.business && paidDeposit) { trackDepositPaid(data.business, paidDeposit.id, paidDeposit.amount, data.business.currency); setPaidDeposit(null); } }, [data, paidDeposit]);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(1);
  const [openCat, setOpenCat] = useState(null);
  const [service, setService] = useState(null);
  const [staff, setStaff] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [slot, setSlot] = useState(null);
  const [details, setDetails] = useState({ client_name: "", client_email: "", client_phone: "", notes: "", marketing_opt_in: false, heard_from: "", referred_by_name: "" });
  const HEARD_OPTIONS = [["instagram", "Instagram"], ["facebook", "Facebook"], ["google", "Google search"], ["tiktok", "TikTok"], ["referral", "A friend recommended you"], ["walk_by", "Saw the clinic"], ["returning", "I'm an existing client"], ["other", "Somewhere else"]];
  const [done, setDone] = useState(false);
  const [refused, setRefused] = useState(null);
  const [creditCovered, setCreditCovered] = useState(0);
  const [embed, setEmbed] = useState(null); // {stripePromise, clientSecret, sessionId}
  const [depositDone, setDepositDone] = useState(false);
  const [bookedId, setBookedId] = useState("");
  const [payLaterDue, setPayLaterDue] = useState(0);
  const [acknowledged, setAcknowledged] = useState(false);
  const [clientUser, setClientUser] = useState(null);
  const [myUpcoming, setMyUpcoming] = useState([]);
  const [myCredit, setMyCredit] = useState(0);
  const loadMine = () => {
    if (!localStorage.getItem("pc_client_token")) { setMyUpcoming([]); setMyCredit(0); return; }
    clientApi.get("/public/client/bookings").then(({ data }) => setMyUpcoming(data.upcoming || [])).catch(() => setMyUpcoming([]));
    clientApi.get("/public/client/account").then(({ data }) => setMyCredit(parseFloat(data.credit_balance) || 0)).catch(() => setMyCredit(0));
  };
  const [authOpen, setAuthOpen] = useState(false);
  const [pubTab, setPubTab] = useState("services");
  const [pkgDetail, setPkgDetail] = useState(null);
  const [myPkgs, setMyPkgs] = useState([]); // packages covering the chosen service (signed-in clients)
  const [usePkg, setUsePkg] = useState("");
  const [pkgPurchased, setPkgPurchased] = useState(false);
  const [wl, setWl] = useState({ client_name: "", client_phone: "" });
  const [wlBusy, setWlBusy] = useState(false);
  const [wlDone, setWlDone] = useState(false);
  const [mode, setMode] = useState("book");
  const [extras, setExtras] = useState([]);
  const [homeCare, setHomeCare] = useState([]);
  const setD = (k, v) => setDetails((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    if (!localStorage.getItem("pc_client_token")) return;
    clientApi.get("/public/client/me").then(({ data }) => {
      setClientUser(data.client);
      loadMine();
      setDetails((d) => ({
        ...d,
        client_name: data.client.name || d.client_name,
        client_phone: data.client.phone || d.client_phone,
        client_email: data.client.email || d.client_email,
      }));
      if (new URLSearchParams(window.location.search).get("account") === "1") setMode("account");
    }).catch(() => localStorage.removeItem("pc_client_token"));
  }, [slug]);

  const onAuthed = (c) => {
    setClientUser(c);
    setDetails((d) => ({ ...d, client_name: c.name || d.client_name, client_phone: c.phone || d.client_phone, client_email: c.email || d.client_email }));
    loadMine();
    setMode("account");
  };
  const clientLogout = () => {
    localStorage.removeItem("pc_client_token");
    setClientUser(null); setMyUpcoming([]); setMyCredit(0);
    setMode("book");
  };

  useEffect(() => {
    publicApi.get(`/public/business/${slug}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (data && data.business.allow_same_day_booking === false && date <= todayStr()) setDate(addDaysStr(1));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dep = params.get("deposit");
    const sid = params.get("session_id");
    const pkgRes = params.get("package");
    if (pkgRes === "success") {
      setPkgPurchased(true); setPubTab("packages");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (pkgRes === "cancelled") {
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (dep === "success" && sid) {
      publicApi.get("/public/deposit/status", { params: { session_id: sid } })
        .then(({ data: d }) => { if (d?.payment_status === "paid") setPaidDeposit({ id: d.booking_id, amount: d.deposit_amount }); setDepositDone(true); setDone(true); }).catch(() => {});
      window.history.replaceState({}, "", window.location.pathname);
    } else if (dep === "cancelled") {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [slug]);

  const cur = data?.business?.currency || "AUD";
  const biz = data?.business;
  const pkgLabels = { bundle: biz?.packages_bundle_label || "Your Bundle", bribes: biz?.packages_bribes_label || "Your Bribes" };
  useEffect(() => {
    if (!clientUser || !service?.id) { setMyPkgs([]); setUsePkg(""); return; }
    clientApi.get("/public/client/package-eligibility", { params: { service_id: service.id } }).then(({ data }) => { setMyPkgs(data.packages || []); setUsePkg(data.packages?.[0]?.id || ""); }).catch(() => setMyPkgs([]));
  }, [clientUser, service?.id]);
  const depositFor = (price) => {
    if (!biz?.deposit_enabled) return 0;
    const p = parseFloat(price) || 0;
    const v = parseFloat(biz.deposit_value) || 0;
    const amt = biz.deposit_type === "fixed" ? Math.min(v, p || v) : (p * v) / 100;
    return Math.round(Math.max(0, amt) * 100) / 100;
  };
  const availStaff = data ? data.staff.filter((s) => !service || s.service_ids.length === 0 || s.service_ids.includes(service.id)) : [];
  const visitTotal = (parseFloat(service?.price) || 0) + extras.reduce((sum, e) => sum + (parseFloat(e.price) || 0), 0);
  const addonServices = data && service && staff
    ? data.services.filter((s) => s.id !== service.id && !extras.some((e) => e.id === s.id) && (staff.service_ids.length === 0 || staff.service_ids.includes(s.id)))
    : [];

  useEffect(() => { if (biz) initTracking(biz); }, [biz?.id]);

  useEffect(() => {
    if (!slug) return;
    const srcKey = `pc_src_${slug}`;
    let src = sessionStorage.getItem(srcKey);
    if (!src) { src = detectTrafficSource(); sessionStorage.setItem(srcKey, src); }
    captureAttribution(slug);
    const k = `pc_visit_${slug}`;
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, "1");
    publicApi.post(`/public/visit/${slug}`, { source: src, campaign: new URLSearchParams(window.location.search).get("utm_campaign") || "" }).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (step === 3 && service && staff) {
      setSlot(null); setWlDone(false);
      setWl((w) => ({ client_name: w.client_name || clientUser?.name || "", client_phone: w.client_phone || clientUser?.phone || "" }));
      publicApi.get("/public/availability", { params: { slug, service_id: service.id, staff_id: staff.id, date } })
        .then(({ data }) => { setSlots(data.slots); setRecommended(data.recommended || []); }).catch(() => { setSlots([]); setRecommended([]); });
    }
  }, [step, date, service, staff, slug]);

  const joinWaitlist = async () => {
    setWlBusy(true);
    try {
      await publicApi.post("/public/waitlist", { slug, client_name: wl.client_name, client_phone: wl.client_phone, client_email: clientUser?.email || "", service_id: service?.id || "", service_ids: [service?.id, ...(extras || []).map((x) => x?.id || x)].filter(Boolean), preferred_date: date });
      setWlDone(true);
    } catch (e) { const r = refusedDetail(e); if (r) setRefused(r); else alert("Sorry, couldn't join the waitlist. Please try again."); }
    finally { setWlBusy(false); }
  };

  const confirm = async (payLater = false) => {
    try {
      const { data: res } = await (clientUser ? clientApi : publicApi).post(`/public/bookings/${slug}`, {
        service_id: service.id, staff_id: staff.id, start: slot, ...details, origin_url: window.location.origin, policy_acknowledged: acknowledged,
        traffic_source: sessionStorage.getItem(`pc_src_${slug}`) || "direct",
        extra_service_ids: extras.map((e) => e.id),
        home_care_interest: homeCare.map((p) => p.id),
        embedded: true,
        pay_later: payLater === true,
        use_package_id: usePkg || null,
        attribution: captureAttribution(slug), heard_from: details.heard_from, referred_by_name: details.referred_by_name || "",
        ...metaBrowserIds(),
      });
      trackBooking(biz, visitTotal, biz?.currency, res.meta_events || {});
      if (res.booking?.id) setBookedId(res.booking.id);
      if (res.pay_later) setPayLaterDue(res.deposit_due || 0);
      if (res.requires_deposit && res.embedded && res.client_secret) {
        const stripePromise = loadStripe(res.publishable_key, { stripeAccount: res.stripe_account });
        setEmbed({ stripePromise, clientSecret: res.client_secret, sessionId: res.session_id });
        return;
      }
      if (res.requires_deposit && res.checkout_url) { window.location.href = res.checkout_url; return; }
      if (res.deposit_covered_by_credit) setCreditCovered(res.deposit_covered_by_credit);
      loadMine();
      setDone(true);
    } catch (e) { const r = refusedDetail(e); if (r) setRefused(r); else alert("Sorry, that time was just taken. Please pick another."); }
  };

  const onDepositComplete = async () => {
    try {
      if (embed?.sessionId) {
        const { data: d } = await publicApi.get("/public/deposit/status", { params: { session_id: embed.sessionId } });
        if (d?.payment_status === "paid") trackDepositPaid(biz, d.booking_id, d.deposit_amount, cur);
      }
    } catch { /* status poll best-effort; webhook/reconciler will confirm */ }
    setEmbed(null);
    setDepositDone(true);
    setDone(true);
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Business not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  if (embed) return (
    <div className="min-h-screen bg-background" data-testid="embedded-checkout">
      <div className="max-w-xl mx-auto px-4 py-4">
        <button onClick={() => setEmbed(null)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="embedded-checkout-close"><ChevronLeft className="h-4 w-4" />Back</button>
        <h2 className="font-heading text-xl font-semibold mb-1">Pay your deposit</h2>
        <p className="text-sm text-muted-foreground mb-4">Your appointment is held while you pay. Apple Pay / card accepted.</p>
        <EmbeddedCheckoutProvider stripe={embed.stripePromise} options={{ clientSecret: embed.clientSecret, onComplete: onDepositComplete }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </div>
    </div>
  );

  const cats = data.categories || [];
  const subs = data.subcategories || [];
  const svcByCat = {};
  const uncategorised = [];
  data.services.forEach((s) => {
    if (s.category_id && cats.find((c) => c.id === s.category_id)) (svcByCat[s.category_id] = svcByCat[s.category_id] || []).push(s);
    else uncategorised.push(s);
  });
  const catGroups = cats
    .filter((c) => (svcByCat[c.id] || []).length > 0)
    .map((c) => {
      const subsOfCat = subs.filter((x) => x.category_id === c.id);
      const bySub = {};
      const noSub = [];
      (svcByCat[c.id] || []).forEach((s) => {
        if (s.subcategory_id && subsOfCat.find((x) => x.id === s.subcategory_id)) (bySub[s.subcategory_id] = bySub[s.subcategory_id] || []).push(s);
        else noSub.push(s);
      });
      return { cat: c, subGroups: subsOfCat.filter((x) => (bySub[x.id] || []).length).map((x) => ({ sub: x, items: bySub[x.id] })), noSub, count: (svcByCat[c.id] || []).length };
    });
  if (uncategorised.length) catGroups.push({ cat: { id: "__uncat", name: "Other services", color: "#6B8E7B" }, subGroups: [], noSub: uncategorised, count: uncategorised.length });

  return (
    <div className="min-h-screen bg-background">
      <RefusedDialog refused={refused} onClose={() => setRefused(null)} />
      <header className="border-b border-border bg-card">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {data.business.logo_url ? (
              <img src={data.business.logo_url.startsWith("/") ? process.env.REACT_APP_BACKEND_URL + data.business.logo_url : data.business.logo_url} alt={data.business.name} className="h-9 w-9 rounded-xl object-cover border border-border" data-testid="public-logo" />
            ) : (
              <img src="/azordi-icon.png" alt="Azordi" className="h-11 w-11 object-contain" data-testid="public-brand-icon" />
            )}
            <div>
              <div className="font-heading font-extrabold">{data.business.name}</div>
              <div className="text-xs text-muted-foreground">Book online</div>
            </div>
          </div>
          {clientUser ? (
            <Button variant={mode === "account" ? "default" : "outline"} size="sm" className="rounded-lg" onClick={() => setMode(mode === "account" ? "book" : "account")} data-testid="client-account-button">
              <UserCircle className="h-4 w-4 mr-1" />{mode === "account" ? "Book" : "My bookings"}
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setAuthOpen(true)} data-testid="client-signin-button">
              <UserCircle className="h-4 w-4 mr-1" />Sign in
            </Button>
          )}
        </div>
      </header>

      <ClientAuthDialog slug={slug} open={authOpen} onOpenChange={setAuthOpen} onAuthed={onAuthed} />

      <div className="max-w-2xl mx-auto px-4 py-8">
        {mode === "account" && clientUser ? (
          <ClientPortal slug={slug} cur={cur} client={clientUser} onClose={() => setMode("book")} onLogout={clientLogout} />
        ) : done ? (
          <Card className="rounded-2xl border bg-card p-10 text-center shadow-sm" data-testid="booking-confirmed">
            <div className="h-14 w-14 rounded-full bg-primary/15 text-primary flex items-center justify-center mx-auto mb-4"><Check className="h-7 w-7" /></div>
            <h2 className="font-heading font-bold text-2xl">You're booked!</h2>
            {depositDone ? (
              <p className="text-muted-foreground mt-2" data-testid="deposit-confirmed">Your deposit has been received — see you soon!</p>
            ) : (
              <>
                <p className="text-muted-foreground mt-2">{service.name} with {staff.name}</p>
                <p className="text-muted-foreground">{fmtDate(date)} at {slot?.slice(11, 16)}</p>
                {creditCovered > 0 && <p className="text-emerald-700 text-sm mt-2" data-testid="deposit-credit-confirmed">Your {fmtCurrency(creditCovered, cur, 2)} deposit was covered by your account credit — nothing more to pay until your visit.</p>}
                {payLaterDue > 0 && <p className="text-sm mt-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-foreground" data-testid="deposit-pay-later-confirmed">Your {fmtCurrency(payLaterDue, cur, 2)} deposit is still to be paid — we've texted you a secure link so you can pay it whenever suits, or pay at your visit.</p>}
              </>
            )}
            <Button className="rounded-lg mt-6" onClick={() => window.location.reload()} data-testid="book-again">Book another</Button>
            {bookedId && slot && (
              <div className="mt-5 flex justify-center"><AddToCalendar icsUrl={`${process.env.REACT_APP_BACKEND_URL}/api/public/bookings/${bookedId}/invite.ics?phone=${encodeURIComponent(details.client_phone || "")}`} title={`${service?.name || "Appointment"} — ${biz?.name || ""}`} start={slot} durationMin={service?.duration || 60} location={biz?.address || ""} details={`${service?.name || "Appointment"} with ${staff?.name || ""} at ${biz?.name || ""}`} testid="confirm-add-to-calendar" /></div>
            )}
            {(biz?.address || biz?.phone) && (
              <div className="mt-6 pt-5 border-t border-border text-sm text-muted-foreground space-y-1" data-testid="confirm-premises">
                {biz.address && <div>{biz.name} · {biz.address}</div>}
                {biz.phone && <div>Call us: <a href={`tel:${biz.phone}`} className="text-primary hover:underline">{biz.phone}</a></div>}
                {biz.website && <div><a href={biz.website} className="text-primary hover:underline" target="_blank" rel="noreferrer">{biz.website}</a></div>}
              </div>
            )}
          </Card>
        ) : (
          <>
            {step > 1 && <button onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-sm text-muted-foreground mb-4" data-testid="booking-back"><ChevronLeft className="h-4 w-4" /> Back</button>}

            {step === 1 && (
              <div data-testid="step-service">
                {clientUser && <RewardsPanel />}
                {clientUser && <UpcomingPanel slug={slug} upcoming={myUpcoming} onChanged={loadMine} />}
                {(data?.packages || []).length > 0 && (
                  <div className="inline-flex rounded-lg border border-border overflow-hidden mb-4" data-testid="pub-tabs">
                    <button onClick={() => setPubTab("services")} className={`px-4 h-10 text-sm font-medium ${pubTab === "services" ? "bg-primary text-primary-foreground" : "bg-card"}`} data-testid="pub-tab-services">Services</button>
                    <button onClick={() => setPubTab("packages")} className={`px-4 h-10 text-sm font-medium ${pubTab === "packages" ? "bg-primary text-primary-foreground" : "bg-card"}`} data-testid="pub-tab-packages">{biz?.packages_tab_name || "Bundles & Bribes"}</button>
                  </div>
                )}
                {pubTab === "packages" ? (
                  <div data-testid="pub-packages">
                    {pkgPurchased && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 mb-4 text-sm text-emerald-900" data-testid="pub-package-purchased">Thank you — your package is paid and has been added to your account. We've texted you the details. Book your first treatment under Services whenever you're ready.</div>}
                    <h2 className="font-heading font-bold text-2xl mb-1">{biz?.packages_tab_name || "Bundles & Bribes"}</h2>
                    <p className="text-sm text-muted-foreground mb-4">Treatment plans with the extras thrown in — what you buy, what we add, what it's worth, what you pay.</p>
                    <div className="space-y-4">{(data?.packages || []).map((p) => <PackageCard key={p.id} plan={p} cur={cur} labels={pkgLabels} onDetails={(pl) => setPkgDetail(pl)} />)}</div>
                    <PackageDetails plan={pkgDetail} cur={cur} labels={pkgLabels} open={!!pkgDetail} onOpenChange={(v) => !v && setPkgDetail(null)} clientUser={clientUser} onNeedSignIn={() => setAuthOpen(true)} />
                  </div>
                ) : (<>
                <h2 className="font-heading font-bold text-2xl mb-1">Choose a service</h2>
                <p className="text-sm text-muted-foreground mb-4" data-testid="booking-location-line">
                  You're booking <span className="font-medium text-foreground">{biz?.name}</span>{biz?.address ? <>, {biz.address}</> : null}.
                  {biz?.phone ? <> Contact: <a href={`tel:${biz.phone}`} className="text-primary hover:underline font-medium" data-testid="booking-contact-phone">{biz.phone}</a></> : null}
                </p>
                <div className="space-y-3">
                  {catGroups.map((g, idx) => {
                    const open = openCat === null ? idx === 0 : openCat === g.cat.id;
                    const renderSvc = (s) => (
                      <button key={s.id} onClick={() => { setService(s); setStaff(null); setExtras([]); setStep(2); }} data-testid={`pub-service-${s.id}`}
                        className="w-full flex items-center justify-between rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors text-left">
                        <div>
                          <div className="font-medium">{s.name}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" />{s.duration_min} min</div>
                        </div>
                        <div className="font-heading font-bold text-primary">{fmtCurrency(s.price, cur, 0)}</div>
                      </button>
                    );
                    return (
                      <div key={g.cat.id} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`pub-cat-${g.cat.id}`}>
                        <button onClick={() => setOpenCat(open ? "__none" : g.cat.id)} data-testid={`pub-cat-toggle-${g.cat.id}`}
                          className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-muted/40 transition-colors">
                          <span className="flex items-center gap-2.5 min-w-0">
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: g.cat.color || "#6B8E7B" }} />
                            <span className="font-heading font-semibold truncate">{g.cat.name}</span>
                            <span className="text-xs text-muted-foreground shrink-0">{g.count}</span>
                          </span>
                          <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                        </button>
                        {open && (
                          <div className="p-3 pt-0 space-y-3">
                            {g.subGroups.map((sg) => (
                              <div key={sg.sub.id}>
                                <div className="font-mono-label text-[11px] uppercase tracking-widest text-muted-foreground px-1 mb-1.5">{sg.sub.name}</div>
                                <div className="space-y-2">{sg.items.map(renderSvc)}</div>
                              </div>
                            ))}
                            {g.noSub.length > 0 && <div className="space-y-2">{g.noSub.map(renderSvc)}</div>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                </>)}
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-staff">
                <h2 className="font-heading font-bold text-2xl mb-4">Choose your therapist</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {availStaff.map((s) => (
                    <button key={s.id} onClick={() => { setStaff(s); setStep(3); }} data-testid={`pub-staff-${s.id}`}
                      className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors">
                      <span className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: s.color }}>{s.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}</span>
                      <span className="font-medium">{s.name}</span>
                    </button>
                  ))}
                  {availStaff.length === 0 && <div className="text-muted-foreground">No therapists available for this service.</div>}
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-time">
                <h2 className="font-heading font-bold text-2xl mb-4">Pick a time</h2>
                <div className="flex items-center gap-2 mb-4">
                  <CalendarDays className="h-4 w-4 text-muted-foreground" />
                  <Input type="date" value={date} min={data.business.allow_same_day_booking === false ? addDaysStr(1) : todayStr()} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-lg w-44" data-testid="pub-date" />
                </div>
                <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-2">Available times</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {slots.map((s) => (
                    <button key={s} onClick={() => { setSlot(s); setStep(4); }} data-testid={`pub-slot-${s.slice(11, 16)}`}
                      className="rounded-lg border border-border bg-card py-2 text-sm hover:border-primary hover:text-primary transition-colors">{s.slice(11, 16)}</button>
                  ))}
                  {slots.length === 0 && (
                    <div className="col-span-full" data-testid="pub-no-slots">
                      <div className="text-muted-foreground text-sm mb-3">No times available on this day.</div>
                      {!wlDone ? (
                        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4" data-testid="waitlist-box">
                          <div className="font-heading font-semibold text-sm mb-1">Join the waitlist</div>
                          <div className="text-xs text-muted-foreground mb-3">We'll text you if a spot opens on {fmtDate(date)}.</div>
                          <div className="grid sm:grid-cols-2 gap-2">
                            <Input placeholder="Your name" value={wl.client_name} onChange={(e) => setWl({ ...wl, client_name: e.target.value })} className="h-10 rounded-lg" data-testid="wl-pub-name" />
                            <Input placeholder="Mobile number" value={wl.client_phone} onChange={(e) => setWl({ ...wl, client_phone: e.target.value })} className="h-10 rounded-lg" data-testid="wl-pub-phone" />
                          </div>
                          <Button className="rounded-lg mt-3 w-full sm:w-auto" disabled={wlBusy || !wl.client_name || !wl.client_phone} onClick={joinWaitlist} data-testid="wl-pub-submit">{wlBusy ? "Joining…" : "Join waitlist"}</Button>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 p-4 text-sm" data-testid="waitlist-done">You're on the waitlist! We'll text you if a spot opens on {fmtDate(date)}.</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="step-details">
                <h2 className="font-heading font-bold text-2xl mb-1">Your details</h2>
                <p className="text-muted-foreground text-sm mb-4">{service.name}{extras.length ? ` + ${extras.length} more` : ""} · {staff.name} · {fmtDate(date)} at {slot?.slice(11, 16)}</p>

                <div className="rounded-lg border border-border p-3 mb-4" data-testid="pub-visit-builder">
                  {myPkgs.length > 0 && (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 mb-3 text-sm" data-testid="pub-package-available">
                      <div className="font-semibold text-primary">You already have {myPkgs[0].sessions_remaining} {service.name} treatment{myPkgs[0].sessions_remaining === 1 ? "" : "s"} available in your package.</div>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Button size="sm" className="rounded-lg" variant={usePkg ? "default" : "outline"} onClick={() => setUsePkg(myPkgs[0].id)} data-testid="pub-use-package"><Check className="h-3.5 w-3.5 mr-1" />Use package</Button>
                        <Button size="sm" className="rounded-lg" variant={!usePkg ? "default" : "outline"} onClick={() => setUsePkg("")} data-testid="pub-pay-instead">Pay for this visit instead</Button>
                        <span className="text-[11px] text-muted-foreground">{myPkgs[0].name}{myPkgs[0].expires_at ? ` · expires ${fmtDate(myPkgs[0].expires_at)}` : ""}</span>
                      </div>
                    </div>
                  )}
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Your visit</div>
                  <div className="flex justify-between text-sm py-0.5" data-testid="pub-visit-primary">
                    <span>{service.name}</span><span className="tabular-nums text-muted-foreground">{fmtCurrency(service.price || 0, cur, 2)}</span>
                  </div>
                  {extras.map((e) => (
                    <div key={e.id} className="flex justify-between items-center text-sm py-0.5" data-testid={`pub-visit-extra-${e.id}`}>
                      <span>{e.name}</span>
                      <span className="flex items-center gap-2">
                        <span className="tabular-nums text-muted-foreground">{fmtCurrency(e.price || 0, cur, 2)}</span>
                        <button onClick={() => setExtras((x) => x.filter((y) => y.id !== e.id))} className="text-muted-foreground hover:text-red-500" data-testid={`pub-extra-remove-${e.id}`} aria-label="Remove"><X className="h-3.5 w-3.5" /></button>
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold border-t border-border mt-1.5 pt-1.5" data-testid="pub-visit-total">
                    <span>Total</span><span className="tabular-nums">{fmtCurrency(visitTotal, cur, 2)}</span>
                  </div>
                  {addonServices.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Add another service to this visit</div>
                      <select value="" onChange={(e) => { const s = addonServices.find((x) => x.id === e.target.value); if (s) setExtras((x) => [...x, s]); }}
                        className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" data-testid="pub-addon-select">
                        <option value="">+ Add a service…</option>
                        {addonServices.map((s) => (
                          <option key={s.id} value={s.id}>{s.name} · {fmtCurrency(s.price || 0, cur, 0)}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {(data.products || []).length > 0 && (
                    <div className="mt-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Home care you're interested in <span className="normal-case font-normal">(optional — we'll have it ready to discuss)</span></div>
                      <select value="" onChange={(e) => { const p = (data.products || []).find((x) => x.id === e.target.value); if (p && !homeCare.some((h) => h.id === p.id)) setHomeCare((x) => [...x, p]); }}
                        className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" data-testid="pub-homecare-select">
                        <option value="">+ Add a home-care product…</option>
                        {(data.products || []).filter((p) => !homeCare.some((h) => h.id === p.id)).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                      {homeCare.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2" data-testid="pub-homecare-chips">
                          {homeCare.map((p) => (
                            <span key={p.id} className="flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs" data-testid={`pub-homecare-chip-${p.id}`}>
                              {p.name}
                              <button onClick={() => setHomeCare((x) => x.filter((y) => y.id !== p.id))} className="hover:text-red-500" aria-label="Remove"><X className="h-3 w-3" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <Input placeholder="Full name" value={details.client_name} onChange={(e) => setD("client_name", e.target.value)} className="h-11 rounded-lg" data-testid="pub-name" />
                  <Input placeholder="Email" value={details.client_email} onChange={(e) => setD("client_email", e.target.value)} className="h-11 rounded-lg" data-testid="pub-email" />
                  <Input placeholder="Phone" value={details.client_phone} onChange={(e) => setD("client_phone", e.target.value)} className="h-11 rounded-lg" data-testid="pub-phone" />
                  <Input placeholder="Notes (optional)" value={details.notes} onChange={(e) => setD("notes", e.target.value)} className="h-11 rounded-lg" data-testid="pub-notes" />
                  {!captureAttribution(slug).utm_source && !captureAttribution(slug).fbclid && (
                    <div className="space-y-1.5" data-testid="pub-heard-from">
                      <div className="text-xs text-muted-foreground">How did you hear about us? <span className="opacity-70">(optional)</span></div>
                      <div className="flex flex-wrap gap-1.5">
                        {HEARD_OPTIONS.map(([k, l]) => (
                          <button type="button" key={k} onClick={() => setD("heard_from", details.heard_from === k ? "" : k)} className={`h-8 px-3 rounded-full border text-xs transition-colors ${details.heard_from === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground hover:bg-muted"}`} data-testid={`pub-heard-${k}`}>{l}</button>
                        ))}
                      </div>
                      {details.heard_from === "referral" && <Input placeholder="Who recommended us? (their name)" value={details.referred_by_name} onChange={(e) => setD("referred_by_name", e.target.value)} className="h-11 rounded-lg" data-testid="pub-referred-by" />}
                    </div>
                  )}
                  <label className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm cursor-pointer" data-testid="pub-marketing">
                    <input type="checkbox" checked={details.marketing_opt_in} onChange={(e) => setD("marketing_opt_in", e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" data-testid="pub-marketing-checkbox" />
                    <span className="text-muted-foreground">Keep me updated with offers, tips and news by email.</span>
                  </label>
                  {clientUser && slot && myUpcoming.some((b) => b.start.slice(0, 10) === slot.slice(0, 10)) && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="pub-sameday-warning">
                      You already have an appointment on this day ({myUpcoming.filter((b) => b.start.slice(0, 10) === slot.slice(0, 10)).map((b) => `${b.service_name} at ${b.start.slice(11, 16)}`).join(", ")}). If you meant to change it, go back and use <b>Reschedule</b> instead of booking twice.
                    </div>
                  )}
                  {depositFor(visitTotal) > 0 && (clientUser && myCredit >= depositFor(visitTotal) ? (
                    <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm flex items-center justify-between" data-testid="pub-deposit-credit-note">
                      <span className="text-emerald-900">Your {fmtCurrency(myCredit, cur, 2)} credit covers the {fmtCurrency(depositFor(visitTotal), cur, 2)} deposit — nothing to pay now.</span>
                      <span className="font-heading font-bold text-emerald-700">{fmtCurrency(0, cur, 2)}</span>
                    </div>
                  ) : clientUser && myCredit > 0 ? (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm flex items-center justify-between" data-testid="pub-deposit-partial-note">
                      <span className="text-muted-foreground">Your {fmtCurrency(myCredit, cur, 2)} credit covers part of the {fmtCurrency(depositFor(visitTotal), cur, 2)} deposit — pay the difference now</span>
                      <span className="font-heading font-bold text-primary">{fmtCurrency(Math.max(0, depositFor(visitTotal) - myCredit), cur, 2)}</span>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm flex items-center justify-between" data-testid="pub-deposit-note">
                      <span className="text-muted-foreground">Deposit due now to confirm{extras.length ? " (covers your whole visit)" : ""}</span>
                      <span className="font-heading font-bold text-primary">{fmtCurrency(depositFor(visitTotal), cur, 2)}</span>
                    </div>
                  ))}
                  {(biz?.address || biz?.cancellation_policy) && (
                    <label className="flex items-start gap-2 rounded-lg border border-border p-3 cursor-pointer" data-testid="pub-policy">
                      <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 accent-primary shrink-0" data-testid="pub-policy-checkbox" />
                      <span className="text-[11px] leading-relaxed text-muted-foreground">
                        {biz?.address && (<><span className="font-medium text-foreground">Appointment location:</span> {biz.address}<br /></>)}
                        {biz?.cancellation_policy && (<><span className="font-medium text-foreground">Cancellation policy:</span> {biz.cancellation_policy}<br /></>)}
                        I confirm I have read and agree to the above.
                      </span>
                    </label>
                  )}
                  <Button className="w-full h-11 rounded-lg" disabled={!details.client_name || ((biz?.cancellation_policy || biz?.address) && !acknowledged)} onClick={() => confirm(false)} data-testid="pub-confirm">
                    {usePkg ? "Confirm booking — using my package" : depositFor(visitTotal) > 0 && !(clientUser && myCredit >= depositFor(visitTotal)) ? `Pay deposit now & book` : "Confirm booking"}
                  </Button>
                  {!usePkg && depositFor(visitTotal) > 0 && biz?.deposit_pay_later && !(clientUser && myCredit >= depositFor(visitTotal)) && (
                    <Button variant="outline" className="w-full h-11 rounded-lg" disabled={!details.client_name || !details.client_phone || ((biz?.cancellation_policy || biz?.address) && !acknowledged)} onClick={() => confirm(true)} data-testid="pub-confirm-pay-later">
                      Book now, pay deposit later — we'll text you a link
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
